import { useState } from 'react';
import axios from 'axios';
import { Search } from 'lucide-react';

const API_V2 = process.env.REACT_APP_BACKEND_URL + '/api/v2';

export function AdminDiagnose({ diagnose, loading }) {
  const [debugQuery, setDebugQuery] = useState('');
  const [debugResults, setDebugResults] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugError, setDebugError] = useState(null);

  const runDebugSearch = async () => {
    if (!debugQuery.trim() || debugQuery.trim().length < 2) return;
    setDebugLoading(true);
    setDebugError(null);
    setDebugResults(null);
    try {
      const res = await axios.get(`${API_V2}/admin/debug-user`, {
        params: { query: debugQuery.trim() },
        withCredentials: true,
      });
      setDebugResults(res.data);
    } catch (err) {
      setDebugError(err.response?.data?.detail || 'Fehler bei der Suche');
    } finally {
      setDebugLoading(false);
    }
  };

  if (!diagnose && !loading) {
    return <div className="text-gray-400">Lade Diagnose-Daten...</div>;
  }
  if (!diagnose) return null;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Datenbank-Diagnose</h2>

      {/* User Debug Search */}
      <div className="bg-gray-800 rounded-xl p-4 space-y-3">
        <h3 className="font-medium text-orange-400 flex items-center gap-2"><Search size={16} /> User-Suche (Debug)</h3>
        <p className="text-sm text-gray-400">Suche nach E-Mail oder Name — zeigt alle Carmas, IDs und Verbindungen.</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={debugQuery}
            onChange={e => setDebugQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runDebugSearch()}
            placeholder="E-Mail oder Name eingeben..."
            data-testid="debug-search-input"
            className="flex-1 rounded-lg bg-gray-700 border border-gray-600 px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <button
            onClick={runDebugSearch}
            disabled={debugLoading || debugQuery.trim().length < 2}
            data-testid="debug-search-btn"
            className="px-4 py-2 text-sm bg-orange-600 hover:bg-orange-700 rounded-lg text-white disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Search size={14} />
            {debugLoading ? 'Suche...' : 'Suchen'}
          </button>
        </div>
        {debugError && <div className="text-red-400 text-sm">{debugError}</div>}

        {debugResults && (
          <div className="space-y-4 mt-3">
            {debugResults.count === 0 && (
              <div className="text-yellow-400 text-sm">Kein User gefunden für &ldquo;{debugResults.query}&rdquo;</div>
            )}
            {debugResults.results.map((r) => (
              <div key={r.user.id} className="bg-gray-900 rounded-lg p-4 space-y-3">
                <div className="border-b border-gray-700 pb-2">
                  <div className="text-white font-medium">{r.user.name || '?'}</div>
                  <div className="text-xs text-gray-400">{r.user.email} · Auth: {r.user.auth_provider} · ID: <span className="font-mono text-orange-400">{r.user.id}</span></div>
                  <div className="text-xs text-gray-500 mt-1">Registriert: {r.user.created_at?.slice(0, 16)} · Digest: {r.user.digest_frequency || '-'} · Instagram: {r.user.instagram_handle || '-'}</div>
                </div>

                <div>
                  <div className="text-sm font-medium text-green-400 mb-1">Empfangen ({r.receivedCount})</div>
                  {r.received.length === 0 && <div className="text-xs text-gray-500">Keine empfangenen Carmas</div>}
                  {r.received.map((m) => (
                    <div key={m.id} className="text-xs text-gray-300 py-1 border-b border-gray-800 flex justify-between gap-2">
                      <span className="truncate flex-1">von <span className="text-blue-400">{m.from}</span>: {m.message}</span>
                      <span className="text-gray-500 whitespace-nowrap">{m.createdAt?.slice(0, 16)}</span>
                    </div>
                  ))}
                </div>

                <div>
                  <div className="text-sm font-medium text-blue-400 mb-1">Gesendet ({r.sentCount})</div>
                  {r.sent.length === 0 && <div className="text-xs text-gray-500">Keine gesendeten Carmas</div>}
                  {r.sent.map((m) => (
                    <div key={m.id} className="text-xs text-gray-300 py-1 border-b border-gray-800 flex justify-between gap-2">
                      <span className="truncate flex-1">an <span className="text-green-400">{m.to}</span>: {m.message}</span>
                      <span className="text-gray-500 whitespace-nowrap">{m.createdAt?.slice(0, 16)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Matching Invites */}
            {debugResults.matchingInvites?.length > 0 && (
              <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-4">
                <div className="text-sm font-medium text-yellow-400 mb-2">Einladungen für &ldquo;{debugResults.query}&rdquo; ({debugResults.matchingInvites.length})</div>
                {debugResults.matchingInvites.map((inv) => (
                  <div key={inv.id} className="text-xs text-gray-300 py-1 border-b border-gray-800 flex justify-between">
                    <span>An: {inv.email} · Von: {inv.inviter_id?.slice(0, 12)}... · Text: {inv.preview_text?.slice(0, 50)}</span>
                    <span className={`px-1.5 rounded ${inv.status === 'claimed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {inv.status}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* V1 Users */}
            {debugResults.v1Users?.length > 0 && (
              <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
                <div className="text-sm font-medium text-red-400 mb-2">Alte v1-Users gefunden ({debugResults.v1Users.length})</div>
                {debugResults.v1Users.map((u, i) => (
                  <div key={i} className="text-xs text-gray-300 py-1">
                    {u.name || '?'} ({u.email || '?'}) · Auth: {u.auth_provider || '?'} · ID: <span className="font-mono text-red-400">{u.id || '?'}</span>
                  </div>
                ))}
                <div className="text-xs text-red-400 mt-2">Diese User sind im alten System registriert, nicht in v2!</div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-gray-800 rounded-xl p-4 space-y-3">
        <h3 className="font-medium text-yellow-400">Verbindung</h3>
        <div className="text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-gray-400">DB Host:</span>
            <span className="text-white font-mono">{diagnose.dbHost}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">DB Name:</span>
            <span className="text-white font-mono">{diagnose.dbName}</span>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-4 space-y-3">
        <h3 className="font-medium text-blue-400">v2 Collections</h3>
        <div className="text-sm space-y-1">
          {Object.entries(diagnose.v2CollectionCounts || {}).map(([name, count]) => (
            <div key={name} className="flex justify-between">
              <span className="text-gray-400 font-mono">{name}</span>
              <span className="text-white font-bold">{count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-4 space-y-3">
        <h3 className="font-medium text-green-400">Daten-Zeitraum</h3>
        <div className="text-sm space-y-2">
          <div>
            <div className="text-gray-400 mb-1">Carma-Nachrichten:</div>
            <div className="flex justify-between pl-3">
              <span className="text-gray-500">Erste:</span>
              <span className="text-white">{diagnose.carmaDateRange?.first ? new Date(diagnose.carmaDateRange.first).toLocaleString('de-DE') : '-'}</span>
            </div>
            <div className="flex justify-between pl-3">
              <span className="text-gray-500">Letzte:</span>
              <span className="text-white">{diagnose.carmaDateRange?.last ? new Date(diagnose.carmaDateRange.last).toLocaleString('de-DE') : '-'}</span>
            </div>
          </div>
          <div>
            <div className="text-gray-400 mb-1">User:</div>
            <div className="flex justify-between pl-3">
              <span className="text-gray-500">Erste/r:</span>
              <span className="text-white">{diagnose.userDateRange?.first ? new Date(diagnose.userDateRange.first).toLocaleString('de-DE') : '-'}</span>
            </div>
            <div className="flex justify-between pl-3">
              <span className="text-gray-500">Letzte/r:</span>
              <span className="text-white">{diagnose.userDateRange?.last ? new Date(diagnose.userDateRange.last).toLocaleString('de-DE') : '-'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-4 space-y-3">
        <h3 className="font-medium text-purple-400">Alle Collections in der DB</h3>
        <div className="text-sm text-gray-400 font-mono space-y-1">
          {diagnose.allCollections?.map((c) => (
            <div key={c}>{c}</div>
          ))}
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-4 space-y-3">
        <h3 className="font-medium text-red-400">DB Schreib-Test</h3>
        <p className="text-sm text-gray-400">Prüft, ob die Datenbank Schreib- und Lesezugriff hat.</p>
        <button
          data-testid="db-write-test-btn"
          onClick={async () => {
            try {
              const res = await axios.post(`${API_V2}/admin/db-test`, {}, { withCredentials: true });
              alert(res.data.success 
                ? `DB Test OK: ${res.data.message}\nTimestamp: ${res.data.timestamp}`
                : `DB Test FEHLGESCHLAGEN: ${res.data.message}`
              );
            } catch (err) {
              alert(`Fehler: ${err.response?.data?.detail || err.message}`);
            }
          }}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium"
        >
          DB-Test ausführen
        </button>
      </div>
    </div>
  );
}
